// Google Ads attribution for landing pages. The click ids from the ad URL are
// kept for 90 days (Google Ads' click window) and sent with the inquiry, so a
// won job can be uploaded back to Google Ads against its click.
const STORAGE_KEY = 'prtwd-ad-click'
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const CLICK_KEYS = ['gclid', 'gbraid', 'wbraid']
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

const ADS_ID = process.env.GATSBY_GOOGLE_ADS_ID || ''

const readStored = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY))
    return saved && Date.now() - saved.at < MAX_AGE_MS ? saved.click : null
  } catch {
    return null
  }
}

// Call once on landing. A new tagged visit replaces the saved click; an
// untagged visit keeps it. Returns the click that applies to this visit.
const captureAdClick = () => {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const click = Object.fromEntries(
    [...CLICK_KEYS, ...UTM_KEYS]
      .map((key) => [key, params.get(key) || ''])
      .filter(([, value]) => value)
  )
  if (!Object.keys(click).length) return readStored() || {}
  click.landing_page = window.location.pathname
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ at: Date.now(), click })
    )
  } catch {
    // Private mode: the click still goes out with this visit's inquiry.
  }
  return click
}

const VISIT_KEY = 'prtwd-visit'

const readVisit = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(VISIT_KEY))
  } catch {
    return null
  }
}

// Call on every page load (gatsby-browser onClientEntry). Saves any ad click,
// and for the first page of the tab session, the landing page and the site
// that sent the visitor. Search engines and AI assistants add no UTM tags, so
// the referrer is the only record of them.
export const captureVisit = () => {
  captureAdClick()
  if (readVisit()) return
  const from = document.referrer ? new URL(document.referrer).hostname : ''
  try {
    window.sessionStorage.setItem(
      VISIT_KEY,
      JSON.stringify({
        landing_page: window.location.pathname,
        referrer: from === window.location.hostname ? '' : from,
      })
    )
  } catch {
    // Storage blocked: inquiries go out without visit details.
  }
}

// Visit details sent with every inquiry; an ad click's landing page wins.
export const leadTracking = () => {
  if (typeof window === 'undefined') return {}
  return { ...readVisit(), ...captureAdClick() }
}

// Loads the Google Ads tag next to GA4 so its conversion linker can read the
// click. No-op until GATSBY_GOOGLE_ADS_ID is set.
export const loadAdsTag = () => {
  if (!ADS_ID || typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') return
  window.gtag('config', ADS_ID)
}

// Reports a lead to Google Ads. `value` is an estimate used for value-based
// bidding; `transactionId` stops a double submit counting twice. The email is
// sent as enhanced-conversion user data, which gtag hashes before sending.
export const trackAdsConversion = ({ label, value, transactionId, email }) => {
  if (!ADS_ID || !label || typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') return
  if (email) window.gtag('set', 'user_data', { email })
  window.gtag('event', 'conversion', {
    send_to: `${ADS_ID}/${label}`,
    value,
    currency: 'USD',
    transaction_id: transactionId,
  })
}
